import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import i18nPackage from "../../i18n/i18n";
import { ProctoringOptionProfileType } from "@verity/database/prisma";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ProfileListProps } from "./ProfileList";
import { PROFILE_CONFIG, ProfileConfigType } from "../edit-quiz-steps/StepIconConfig";

export type PresetProfileType = "standard" | "advanced" | "essential";

export function getPresetKey(profile: ProctoringOptionProfileV2): PresetProfileType | null {
	if (profile.type !== "preset") return null;

	if (profile.name?.toLowerCase().includes("standard")) return "standard";
	if (profile.name?.toLowerCase().includes("advanced")) return "advanced";
	if (profile.name?.toLowerCase().includes("essential")) return "essential";

	return null;
}

export function getUIConfigForProfile(profile: ProctoringOptionProfileV2): ProfileConfigType | null {
	const { type } = profile;

	// Handle non-preset types (Course, Institute, User)
	if (type === "course" || type === "institute" || type === "user") {
		return PROFILE_CONFIG[type];
	}

	const presetType = getPresetKey(profile);

	return presetType ? PROFILE_CONFIG.preset[presetType] : null;
}

export function getProfileName(profile?: ProctoringOptionProfileV2) {
	return profile?.name ?? i18nPackage.t("editQuizPage.profileStep.unknownProfile");
}

/**Standard, Advanced, Essential */
export function sortPresetProfiles(profiles: ProctoringOptionProfileV2[]) {
	const presetProfiles = profiles.filter((p) => p.type === "preset");

	const standard = presetProfiles.find((p) => getPresetKey(p) === "standard");
	const advanced = presetProfiles.find((p) => getPresetKey(p) === "advanced");
	const essential = presetProfiles.find((p) => getPresetKey(p) === "essential");

	return [standard, advanced, essential].filter((p): p is ProctoringOptionProfileV2 => !!p);
}

export type ProfileFilterType = Extract<ProctoringOptionProfileType, "preset" | "institute" | "course" | "user">;

export function useFilteredProfiles(profiles: ProctoringOptionProfileV2[], enforcedProfilesOnly: boolean) {
	const [appliedFilters, setAppliedFilters] = useState<ProfileFilterType[]>([]);
	const [pendingFilters, setPendingFilters] = useState<ProfileFilterType[]>([]);
	const [searchText, setSearchText] = useState("");
	const searchFilter = searchText.toLowerCase();

	const handleClearFilter = useCallback(() => {
		setPendingFilters([]);
	}, []);

	const handleApplyFilter = useCallback(() => {
		setAppliedFilters(pendingFilters);
	}, [pendingFilters]);

	const handleFilterCheckbox = useCallback(
		(profileType: ProfileFilterType, checked: boolean) => {
			const newProfileFilter = pendingFilters.filter((p) => p !== profileType);
			setPendingFilters(checked ? newProfileFilter.concat([profileType]) : newProfileFilter);
		},
		[pendingFilters],
	);

	const isFilterChecked = useCallback(
		(profileType: ProfileFilterType) => pendingFilters.includes(profileType),
		[pendingFilters],
	);

	const isFilterApplied = useCallback(
		(profileType: ProfileFilterType) => appliedFilters.includes(profileType),
		[appliedFilters],
	);

	const filteredProfiles = useMemo(() => {
		const filtered = profiles.filter((profile) => {
			const matchesSearch = !searchFilter || getProfileName(profile).toLowerCase().includes(searchFilter);
			const matchesFilter = appliedFilters.length === 0 || appliedFilters.includes(profile.type as ProfileFilterType);
			const matchesEnforceProfiles = enforcedProfilesOnly ? profile.instituteProfile?.enforced : true;

			return matchesSearch && matchesFilter && matchesEnforceProfiles;
		});
		return filtered;
	}, [appliedFilters, enforcedProfilesOnly, profiles, searchFilter]);

	const areFiltersApplied = useMemo(() => {
		return searchText.trim() !== "" || appliedFilters.length !== 0;
	}, [appliedFilters.length, searchText]);

	return {
		filteredProfiles,
		filter: {
			isFilterChecked,
			isFilterApplied,
			handleFilterCheckbox,
			handleApplyFilter,
			filterCount: appliedFilters.length,
			handleClearFilter,
			areFiltersApplied,
		},
		search: {
			setSearchText,
			searchText,
		},
	};
}

type UseFilteredProfilesReturn = ReturnType<typeof useFilteredProfiles>;

/**How many profiles to show on first mount not including custom config button */
const INITIAL_COUNT = 3;

/**How many more profiles to show when the user clicks see more */
const INCREMENT_COUNT = 17;

export function usePaginatedProfiles(
	allProfiles: ProfileListProps["profiles"],
	selectedId: ProfileListProps["selectedId"],
	showCustomButton: ProfileListProps["showCustomButton"],
	isFiltersApplied: boolean,
) {
	const finalInitialCount = showCustomButton ? INITIAL_COUNT : INITIAL_COUNT + 1;

	const [limit, setLimit] = useState(finalInitialCount);
	const [isAtBottom, setIsAtBottom] = useState(false); // Track scroll position
	const gridRef = useRef<HTMLDivElement>(null);

	const isExpanded = useMemo(() => {
		return limit > finalInitialCount || isFiltersApplied;
	}, [finalInitialCount, limit, isFiltersApplied]);

	const handleViewMore = useCallback(() => {
		setLimit((prev) => {
			const nextLimit = prev + INCREMENT_COUNT;
			const selectedIsPreset = allProfiles.find((p) => p.id === selectedId)?.type === "preset";

			// Calculate total items that will be in the grid
			const total = nextLimit + (selectedIsPreset ? 1 : 0);

			// If the total is odd, add 1 to balance the row
			if (total % 2 !== 0) {
				return nextLimit + 1;
			}
			return nextLimit;
		});
	}, [allProfiles, selectedId]);

	const handleScroll = useCallback(() => {
		if (!gridRef.current) return;

		const { scrollTop, scrollHeight, clientHeight } = gridRef.current;
		const reachedBottom = scrollHeight - scrollTop <= clientHeight + 100;
		setIsAtBottom(reachedBottom);
	}, []);

	// Reset bottom status when limit changes or expansion toggles
	useEffect(() => {
		if (isExpanded) {
			handleScroll();
		} else {
			setIsAtBottom(false);
		}
	}, [isExpanded, limit, handleScroll]);

	return {
		handleScroll,
		handleViewMore,
		limit,
		isAtBottom,
		gridRef,
		isExpanded,
	};
}

export function useSortedProfiles(
	filteredProfiles: UseFilteredProfilesReturn["filteredProfiles"],
	selectedId: ProfileListProps["selectedId"],
	limit: number,
	isFilterApplied: UseFilteredProfilesReturn["filter"]["isFilterApplied"],
) {
	const initialSelectedIdOnMount = useRef(selectedId);
	const sortedProfiles = useMemo(() => {
		// Find who should be at the top based on what was selected when the page loaded
		const topId = initialSelectedIdOnMount.current;
		const isPresetFilterActive = isFilterApplied("preset");

		const filtered = filteredProfiles.filter((p) => {
			// Keep it if:
			// 1. It's the selected item at the top
			// 2. It's NOT a preset
			// 3. It IS a preset, BUT the user specifically filtered for presets
			return p.id === topId || p.type !== "preset" || isPresetFilterActive;
		});

		// Put the selected profile at the top
		if (topId) {
			const list = [...filtered];
			const idx = list.findIndex((p) => p.id === topId);
			if (idx > 0) {
				const [item] = list.splice(idx, 1);
				list.unshift(item);
			}
			return list;
		}
		return filtered;
	}, [filteredProfiles, isFilterApplied]);

	// Get the presets for the bottom
	const bottomPresets = useMemo(() => {
		const topId = initialSelectedIdOnMount.current;

		// If the user filtered for presets, they are now in the mainFlow.
		// To avoid duplicates, we hide the bottom section if a preset filter is active.
		if (isFilterApplied("preset")) return [];

		return filteredProfiles.filter((p) => p.type === "preset" && p.id !== topId);
	}, [isFilterApplied, filteredProfiles]);

	// Determine what's visible now
	const visibleProfiles = useMemo(() => sortedProfiles.slice(0, limit), [limit, sortedProfiles]);

	const hasMoreProfiles = useMemo(() => sortedProfiles.length > limit, [limit, sortedProfiles.length]);

	return {
		bottomPresets,
		visibleProfiles,
		hasMoreProfiles,
	};
}

export function useProfileModals(filteredProfiles: UseFilteredProfilesReturn["filteredProfiles"]) {
	const [viewModalId, setViewModalId] = useState<number | undefined>(undefined);
	const [deleteModalId, setDeleteModalId] = useState<number | undefined>(undefined);

	const activeViewProfile = useMemo(
		() => filteredProfiles.find((p) => p.id === viewModalId),
		[filteredProfiles, viewModalId],
	);
	const activeDeleteProfile = useMemo(
		() => filteredProfiles.find((p) => p.id === deleteModalId),
		[deleteModalId, filteredProfiles],
	);

	const openViewModal = useCallback((id: ProfileListProps["selectedId"]) => setViewModalId(id), []);
	const closeViewModal = useCallback(() => setViewModalId(undefined), []);

	const openDeleteModal = useCallback((id: ProfileListProps["selectedId"]) => setDeleteModalId(id), []);
	const closeDeleteModal = useCallback(() => setDeleteModalId(undefined), []);

	return {
		activeDeleteProfile,
		activeViewProfile,
		openViewModal,
		closeViewModal,
		openDeleteModal,
		closeDeleteModal,
	};
}
